<template>
  <div>
    <component :is="currentStepComponent"
      v-model="currentModel"
      :quote="quote"
      :checked="currentStatus.checked"
      :checkLoading="checkLoading"
      :validated="currentStatus.validated"
      :onSubmit="onSubmit"
      @check="onCheck"
      @next="onNext"
      @reset="onReset"
      />
  </div>
</template>
<script>
import api from '@/api/api'
import Amount from '@/views/ProtectWizard/Amount.vue'
import Dates from '@/views/ProtectWizard/Dates.vue'
import Debtor from '@/views/ProtectWizard/Debtor.vue'
import Creditor from '@/views/ProtectWizard/Creditor.vue'
import Product from '@/views/ProtectWizard/Product.vue'

const WIZARD_ORDER = [
  'amount',
  'debtor',
  'dates',
  'creditor'
]

const WIZARD_COMPONENTS = {
  amount: Amount,
  debtor: Debtor,
  dates: Dates,
  product: Product,
  creditor: Creditor
}

const UW_RULES_MAP = {
  amount: [
    'O4',
    'O5'
  ],
  debtor: [
    // 'B1',
    'B2',
    'B3',
    'B4',
    'B5',
    'B6',
    'B7',
    'B8',
    'B9',
    'B11',
    'B12',
    'B13',
    'B14',
    // 'B16',
    'B17',
    // 'B17a',
    // 'B17b',
    'B18',
    'O9a',
    'O9b',
    'O9c',
    'O10',
    'O11'
    // 'O12'
  ],
  dates: [
    'O3',
    'O6',
    'O7'
  ],
  creditor: [
    /*
    // 'S1',
    'S2',
    'S3',
    'S4',
    'S5',
    'S6',
    'S7',
    'S8',
    'S9',
    'S10',
    'S11',
    'S12',
    'S14a',
    'S14b',
    // 'S15',
    'S16',
    'B10',
    'O1',
    'O2',
    'O8a',
    'O8b'
    */
  ]
}

const DEFAULT_TRANSACTION = {
  amount: 0,
  product: '',
  dates: null,
  debtor: null,
  creditor: null
}

export default {
  name: 'protect-wizard',
  data: function () {
    return {
      debug: false,
      transaction: { ...DEFAULT_TRANSACTION },
      quote: null,
      uwChecks: {},
      checkLoading: false
    }
  },
  computed: {
    transactionId () { return this.$store.state.protectWizard.transaction.id },
    transactionKey () { return this.$store.state.protectWizard.transaction.key },
    currentStepChecked () { return this.$store.state.protectWizard.check },
    currentStepIndex () { return this.$store.state.protectWizard.step },
    status () {
      return {
        amount: this.transaction.amount > 0,
        dates: !!(this.transaction.dates),
        // product: !!this.transaction.product,
        debtor: !!this.transaction.debtor,
        creditor: !!this.transaction.creditor
      }
    },
    allValid () {
      return Object.values(this.status).every(i => !!i)
    },
    currentStepName () {
      return WIZARD_ORDER[this.currentStepIndex]
    },
    currentStepComponent () {
      return WIZARD_COMPONENTS[this.currentStepName]
    },
    currentStepUWRules () {
      return UW_RULES_MAP[this.currentStepName].join(' ')
    },
    currentModel: {
      get () { return this.transaction[this.currentStepName] },
      set (value) { this.transaction[this.currentStepName] = value }
    },
    currentStatus () {
      let checked = this.currentStepChecked ? {
        ready: this.checkLoading,
        ...this.uwChecks[this.currentStepName]
      } : null

      return {
        validated: this.status[this.currentStepName],
        checked
      }
    },
    transactionPayload () {
      const payload = {
        currency: this.transaction.currency || process.env.VUE_APP_DEFAULT_CURRENCY,
        net_amount: this.transaction.amount
      }

      if (this.transaction.dates) {
        const [ issueDate, dueDate ] = this.transaction.dates
        payload.issue_date = issueDate
        payload.due_date = dueDate
      }

      if (this.transaction.debtor) {
        payload.debtor_name = this.transaction.debtor.title
        payload.debtor_regnum = this.transaction.debtor.company_number
        payload.debtor_address = this.transaction.debtor.address_snippet
        payload.debtor_country = this.transaction.debtor.country || process.env.VUE_APP_DEFAULT_DEBTOR_COUNTRY
      }

      if (this.transaction.creditor) {
        payload.creditor_name = this.transaction.creditor.title
        payload.creditor_regnum = this.transaction.creditor.company_number
        payload.creditor_address = this.transaction.creditor.address_snippet
        payload.creditor_country = this.transaction.creditor.country || process.env.VUE_APP_DEFAULT_CREDITOR_COUNTRY
      }

      // Hotfix for https://gitlab.com/hokodo/product/issues/474
      // Entire component will likely be rewritten, so for now just applying a band-aid
      if (this.currentStepName === 'debtor') {
        payload.debtor = null // Set debtor null to inform API to do matching on the name/company number
      } else if (this.currentStepName === 'creditor') {
        payload.creditor = null // Set creditor null to inform API to do matching on the name/company number
      }

      return payload
    }
  },
  methods: {
    onSubmit () {
      const [issueDate, dueDate] = this.transaction.dates || [null, null]
      const trackingPayload = {
        step: this.currentStepName,
        net_amount: this.transaction.amount,
        product: this.transaction.product,
        debtor_name: this.transaction.debtor,
        creditor_name: this.transaction.creditor,
        issue_date: issueDate,
        due_date: dueDate,
        currency: 'GBP'
      }
      if (this.transaction.debtor) {
        trackingPayload.debtor_regnum = this.transaction.debtor.company_number
        trackingPayload.debtor_address = this.transaction.debtor.address_snippet
      }
      if (this.transaction.creditor) {
        trackingPayload.creditor_regnum = this.transaction.creditor.company_number
        trackingPayload.creditor_address = this.transaction.creditor.address_snippet
      }

      window.analytics.track('Buy First Policy - Transaction updated', trackingPayload)
      return this.updateTransaction()
    },
    checkUnderwriting () {
      if (this.currentStepUWRules.length > 0) {
        return api.checkUnderwriting({
          transaction: this.transactionId,
          uw_rules: this.currentStepUWRules
        })
      } else {
        return Promise.resolve({
          status: 'passed'
        })
      }
    },
    onCheck (check) {
      // FIXME: this part is too complex and mixes step-by-step UW and
      // quote generation.
      if (check) {
        this.checkLoading = true
        this.checkUnderwriting().then((uwCheck) => {
          this.uwChecks[this.currentStepName] = uwCheck

          // This is only a bandaid to solve https://gitlab.com/hokodo/product/issues/375
          // In future, I would recommend rewriting this component using Vuex actions
          if (this.currentStepName === 'debtor') {
            this.getTransaction()
          }

          // FIXME: this should be done only at the last step
          // Or ideally, it should be a condition on the payment view
          if (check && this.allValid) {
            // get quote if everything is valid
            return api.getQuote({
              transaction: this.transactionId
            }).then(quote => {
              this.quote = quote
              if (this.quote.status === 'declined' || this.quote.status === 'expired') {
                // NOTE: a declined quote seems to be an irresolvable state
                // trash the current transaction and start again
                this.$store.commit('protectWizard/DIE')
              } else {
                this.$store.commit('protectWizard/ESTIMATE', { total_price: this.quote.total_price })
              }
            })
          }
        }).then(() => {
          // Once the check is performed, commit to state
          this.$store.commit('protectWizard/CHECK', check)
        }).finally(() => {
          this.checkLoading = false
        })
      } else {
        this.$store.commit('protectWizard/CHECK', check)
      }
    },
    onNext () {
      window.analytics.track('Buy First Policy - Step Completed', {
        step: this.currentStepName
      })

      const nextIndex = this.currentStepIndex + 1
      if (nextIndex >= WIZARD_ORDER.length) {
        this.$router.push({
          name: 'payment',
          query: {
            id: this.transactionId,
            key: this.transactionKey
          }
        })
      } else {
        this.$store.commit('protectWizard/STEP_UPDATE', nextIndex)
        this.$store.commit('protectWizard/CHECK', false)
      }
    },
    onReset () {
      this.initializeTransaction()
    },
    initializeTransaction () {
      return this.$store.dispatch('protectWizard/initializeTransaction').then(data => {
        this.transaction = { ...DEFAULT_TRANSACTION }
        this.quote = null
        this.uwChecks = {}
      })
    },
    updateTransaction () {
      return api.patchTransaction(this.transactionId, this.transactionPayload)
        .then(response => {
          this.$store.commit('protectWizard/ESTIMATE', response.estimated_quote_price)
        })
    },
    getTransaction () {
      return api.getTransaction(this.transactionId)
        .then(response => {
          this.$store.commit('protectWizard/ESTIMATE', response.estimated_quote_price)
        })
    }
  },
  mounted () {
    // initialize transaction pipeline
    this.initializeTransaction()
  }
}
</script>
